import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetProductQuery } from '../../Store/api/apiSlice';
import { getCategoryBreadcrumbTrail } from '../../utils/categoryTree';
import { resolveCategoryId } from '../../utils/urls';
import styles from '../../styles/Breadcrumbs.module.scss';

const HOME_CRUMB = { label: 'Головна', path: '/' };

const STATIC_TRAILS = {
  '/cart': [{ label: 'Кошик' }],
  '/checkout': [
    { label: 'Кошик', path: '/cart' },
    { label: 'Оформлення замовлення' },
  ],
  '/profile': [{ label: 'Редагування профілю' }],
  '/favorites': [{ label: 'Обране' }],
  '/orders': [{ label: 'Мої замовлення' }],
  '/search': [{ label: 'Результати пошуку' }],
  '/delivery': [{ label: 'Доставка' }],
  '/payment': [{ label: 'Оплата' }],
};

const Breadcrumbs = () => {
  const location = useLocation();
  const { slugOrId } = useParams();
  const { list: categories } = useSelector((state) => state.categories);
  const categoryId = resolveCategoryId(categories, slugOrId);

  const isProductPage = /^\/products\/[^/]+/.test(location.pathname);
  const isCategoryPage = /^\/categories\/[^/]+/.test(location.pathname);

  const { data: product } = useGetProductQuery({ id: slugOrId }, { skip: !isProductPage || !slugOrId });

  const items = useMemo(() => {
    if (location.pathname === '/') {
      return null;
    }

    if (isCategoryPage) {
      const categoryTrail = getCategoryBreadcrumbTrail(categories, categoryId).map((category) => ({
        label: category.name,
        path: category.path,
      }));

      if (!categoryTrail.length) {
        return null;
      }

      return [HOME_CRUMB, ...categoryTrail];
    }

    if (isProductPage) {
      if (!product) {
        return [HOME_CRUMB, { label: 'Завантаження…' }];
      }

      const categoryTrail = getCategoryBreadcrumbTrail(categories, product.category?.id).map(
        (category) => ({
          label: category.name,
          path: category.path,
        })
      );

      return [
        HOME_CRUMB,
        ...categoryTrail,
        { label: product.title },
      ];
    }

    const staticTrail = STATIC_TRAILS[location.pathname];
    if (staticTrail) {
      return [HOME_CRUMB, ...staticTrail];
    }

    if (location.pathname !== '/') {
      return [HOME_CRUMB, { label: 'Сторінку не знайдено' }];
    }

    return null;
  }, [categories, categoryId, isCategoryPage, isProductPage, location.pathname, product]);

  if (!items?.length) {
    return null;
  }

  return (
    <nav className={styles.breadcrumbs} aria-label="Навігаційний ланцюжок">
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={`${item.label}-${index}`} className={styles.item}>
              {index > 0 && <span className={styles.separator} aria-hidden="true">/</span>}
              {item.path && !isLast ? (
                <Link className={styles.link} to={item.path}>
                  {item.label}
                </Link>
              ) : (
                <span className={isLast ? styles.current : styles.text} aria-current={isLast ? 'page' : undefined}>
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
